import axios from 'axios';
import request from './request';
import type { HinhThucThanhToan } from '../components/BanHang/TinhTienModal';

type ApiErrorResponse = {
  message?: string;
};

const getDishMutationError = (error: unknown, fallback: string): Error => {
  if (axios.isAxiosError<ApiErrorResponse>(error)) {
    const message = error.response?.data?.message;
    if (message) return new Error(message);
  }

  return error instanceof Error ? error : new Error(fallback);
};

export type DatBanPayload = {
  ban_id: number;
  ghi_chu?: string;
  chi_tiet_dat_ban: Array<{
    hang_hoa_id: number;
    so_luong: number;
    ghi_chu?: string;
  }>;
};

type DatBanResponse = {
  success: boolean;
  message?: string;
  datBan?: { id: number; ban_id: number; nhan_vien_id: number };
};

type ChuyenBanResponse = {
  success: boolean;
  message?: string;
  soDatBanDuocChuyen?: number;
};

type GiamMonDatBanResponse = {
  success: boolean;
  message?: string;
  soLuongConLai?: number;
  banTrong?: boolean;
};

type CapNhatSoLuongMonDatBanResponse = {
  success: boolean;
  message?: string;
  chiTietDatBan?: {
    id: number;
    so_luong: number;
  };
};

type ThanhToanDatBanResponse = {
  success: boolean;
  message?: string;
  tongTien?: number;
  xuatKho?: { id: number; key_value: string; tong_so_tien_thanh_toan: number };
};

const datBanService = {
  create: async (data: DatBanPayload): Promise<DatBanResponse> => {
    const response = await request.post<DatBanResponse>('user-sale-dat-ban', data);
    const result = response.data;

    if (!result.success) {
      throw new Error(result.message || 'Không thể thêm món vào bàn');
    }

    return result;
  },
  chuyenBan: async (banNguonId: number, banDichId: number): Promise<ChuyenBanResponse> => {
    const response = await request.post<ChuyenBanResponse>('user-sale-chuyen-ban', {
      ban_nguon_id: banNguonId,
      ban_dich_id: banDichId,
    });
    const result = response.data;

    if (!result.success) {
      throw new Error(result.message || 'Không thể chuyển bàn');
    }

    return result;
  },
  giamMon: async (banId: number, chiTietDatBanId: number): Promise<GiamMonDatBanResponse> => {
    try {
      const response = await request.post<GiamMonDatBanResponse>('user-sale-giam-mon-dat-ban', {
        ban_id: banId,
        chi_tiet_dat_ban_id: chiTietDatBanId,
      });
      const result = response.data;

      if (!result.success) {
        throw new Error(result.message || 'Không thể giảm món trong đơn');
      }

      return result;
    } catch (error) {
      throw getDishMutationError(error, 'Không thể giảm món trong đơn');
    }
  },
  capNhatSoLuongMon: async (
    banId: number,
    chiTietDatBanId: number,
    soLuong: number
  ): Promise<number> => {
    try {
      const response = await request.post<CapNhatSoLuongMonDatBanResponse>(
        'user-sale-cap-nhat-so-luong-mon-dat-ban',
        {
          ban_id: banId,
          chi_tiet_dat_ban_id: chiTietDatBanId,
          so_luong: soLuong,
        }
      );
      const result = response.data;

      if (!result.success || !result.chiTietDatBan) {
        throw new Error(result.message || 'Không thể cập nhật số lượng món');
      }

      return result.chiTietDatBan.so_luong;
    } catch (error) {
      throw getDishMutationError(error, 'Không thể cập nhật số lượng món');
    }
  },
  xoaMon: async (banId: number, chiTietDatBanId: number): Promise<GiamMonDatBanResponse> => {
    try {
      const response = await request.post<GiamMonDatBanResponse>('user-sale-xoa-mon-dat-ban', {
        ban_id: banId,
        chi_tiet_dat_ban_id: chiTietDatBanId,
      });
      const result = response.data;

      if (!result.success) {
        throw new Error(result.message || 'Không thể xóa món khỏi đơn');
      }

      return result;
    } catch (error) {
      throw getDishMutationError(error, 'Không thể xóa món khỏi đơn');
    }
  },
  thanhToan: async (banId: number, hinhThucThanhToan: HinhThucThanhToan): Promise<ThanhToanDatBanResponse> => {
    const response = await request.post<ThanhToanDatBanResponse>('user-sale-thanh-toan-dat-ban', {
      ban_id: banId,
      hinh_thuc_thanh_toan: hinhThucThanhToan,
    });
    const result = response.data;

    if (!result.success) {
      throw new Error(result.message || 'Không thể xác nhận thanh toán');
    }

    return result;
  },
};

export default datBanService;